import Cropper from 'libraries/cropper.js';
import events from 'events.js';
import http from 'http.js';
import ko from 'knockout';

const imageCropperTemplate = `
<!-- ko if: supported -->
    <!-- ko if: imageUrl -->
        <div class="row">
            <div class="col-xs-12 padding-sm image-recrop">
                <h3 class="text-hidden">Crop your profile photo</h3>
                <p class="text-center helper">
                    <small>
                        To crop this image, drag the crop tool to the desired
                        area below and then click Upload this Image.
                    </small>
                </p>
                <div class="crop-image-wrapper">
                    <img
                        class="image-preview"
                        data-bind="attr: {src: imageUrl}">
                </div>
                <div
                    class="progress"
                    data-bind="
                        css: {'hide': !uploading()},
                        style: {'width': imageWidth() + 'px'}
                ">
                    <div
                        class="
                            progress-bar
                            progress-bar-success
                            progress-bar-striped
                            active
                        "
                        role="progressbar"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        data-bind="
                            attr: {'aria-valuenow': uploadProgress},
                            style: {'width': uploadProgress() + '%'}
                        "
                    >
                        <span
                            class="sr-only"
                            data-bind="text: uploadProgress() + '%'">
                        </span>
                    </div>
                </div>
                <div class="btn-holder clearfix">
                    <button
                        class="
                            btn
                            btn-default
                            pull-left
                            hidden-xs
                            margin-xs-top
                        "
                        data-bind="click: selectDifferentImage
                    ">
                        Select a different image
                    </button>
                    <button
                        class="
                            btn
                            btn-default
                            pull-left
                            visible-xs
                            margin-xs-top
                        "
                        data-bind="click: function() {
                            selectDifferentImage();
                            openFileDialog();
                        }
                    ">
                        Select a different image
                    </button>
                    <button
                        class="btn btn-primary pull-left margin-xs-top"
                        data-bind="enable: !uploading(), click: upload"
                        id="uploadPhoto"
                    >
                        Upload this Image
                    </button>
                    <!-- ko if: message -->
                        <p class="margin-xs-top" data-bind="text: message"></p>
                    <!-- /ko -->
                    <!-- ko if: error -->';
                        <p
                            class="error margin-xs-top"
                            data-bind="text: error"
                        ></p>
                    <!-- /ko -->
                </div>
            </div>
        </div>
    <!-- /ko -->
    <!-- ko ifnot: imageUrl -->
        <div class="row">
            <div
                class="col-xs-12 text-center padding-md"
                data-bind="
                    css: {'dragover-highlight': dragHover},
                    event: {
                        'dragover': dragOver,
                        'dragenter': dragEnter,
                        'dragleave': dragLeave,
                        'drop': drop,
                    }
                "
            >
                <span
                    class="text-muted"
                    data-bind="
                        css: {'dragover-highlight-text': dragHover()}
                    "
                >
                    <i class="fa fa-file-image-o fa-4x"></i>
                </span>
                <h4
                    class="text-muted"
                    data-bind="css: {'dragover-highlight-text': dragHover()}"
                >
                    Drag your photo here to upload it.
                </h4>
                <p>or</p>
                <button
                    class="btn btn-default btn-sm clicker"
                    data-bind="click: openFileDialog"
                >
                    Select a photo from your computer
                </button>
                <!-- ko if: error -->
                    <p
                        class="error text-center margin-sm-top"
                        data-bind="text: error"
                    ></p>
                <!-- /ko -->
            </div>
        </div>
        <input
            type="file"
            data-bind="event: {'change': setFileInput}"
            style="position: fixed; top:-1000px;"
            id="file">
    <!-- /ko -->
    <!-- ko ifnot: supported -->
        <p>
            Your browser doesn't support HTML 5 file uploads. Please try using
            a modern browser, or email your avatar to
            <a href="mailto:hello@healthshare.com.au">HealthShare Support</a>.
        </p>
    <!-- /ko -->
<!-- /ko -->`;

class ImageCropperModel {
    constructor(params, elem) {
        this.params = params;
        this.elem = elem;
        this.image = params.image;
        this.imageUrl = ko.observable();
        this.imageWidth = ko.observable();
        this.coords = params.coords;
        this.supportedFileTypes = ['image/jpeg', 'image/png', 'image/gif'];
        this.fileExtensions = {
            'image/jpeg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
        };
        this.maxFileSize = 4 * 1024 * 1024;
        this.maxWidth = ko.unwrap(params.width) || 800;
        this.maxHeight = ko.unwrap(params.height) || 400;
        this.minWidth = ko.unwrap(params.cropWidth) || 100;
        this.minHeight = ko.unwrap(params.cropHeight) || 100;
        this.uploadUrl = params.uploadUrl;
        this.cropUrl = params.cropUrl;
        this.supported = ko.observable(true);
        this.error = ko.observable('');
        this.message = ko.observable('');
        // Track drag enter / leaves (see below for why)
        this.dragCounter = ko.observable(0);
        this.dragHover = ko.pureComputed(() => this.dragCounter() > 0);
        this.adjustRatio = ko.observable(1);
        this.uploading = ko.observable(false);
        this.uploadProgress = ko.observable(0);
        // Track if we need to upload
        this.imageUploaded = ko.observable(false);

        if (
            typeof FileReader === 'undefined' ||
            typeof FormData === 'undefined'
        ) {
            this.supported(false);
            return;
        }

        this.reader = new FileReader();

        this.reader.onloadend = async () => {
            await events.ready();
            // Ensure image won't exceed maximum dimensions
            const img = new Image();
            img.onload = () => {
                this.processImage(img);
            };
            img.src = this.reader.result;
        };

        this.dragOver = this.dragOver.bind(this);
        this.dragEnter = this.dragEnter.bind(this);
        this.dragLeave = this.dragLeave.bind(this);
        this.handleProgress = this.handleProgress.bind(this);

        // Check if avatar passed in is loaded already
        if (this.image()) {
            this.imageUploaded(true);
            this.processImage(this.image());
        }
    }

    openFileDialog() {
        this.elem.querySelector('input[type=file]').click();
    }

    selectDifferentImage() {
        this.image(null);
        this.imageUrl('');
        this.imageWidth(null);
        this.coords(null);
        this.message('');
        this.error('');
        this.dragCounter(0);
        this.adjustRatio(1);
        this.uploadProgress(0);
        this.imageUploaded(false);
    }

    handleProgress(event) {
        if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            this.uploadProgress(Math.round(progress));
        }
    }

    async upload() {
        this.message('');
        if (this.imageUploaded()) {
            await this.uploadCropping();
        } else {
            this.uploading(true);
            this.uploadProgress(0);
            const url = ko.utils.unwrapObservable(this.uploadUrl);
            const data = new FormData();
            const filename = this.image().name;
            data.append('avatar', this.image(), filename);
            try {
                await http.post({
                    url,
                    data,
                    handleProgress: this.handleProgress,
                });
                await this.uploadCropping();
                this.image(
                    this.elem.getElementsByClassName('image-preview')[0],
                );
                this.imageUploaded(true);
            } catch (ex) {
                this.error('Error uploading avatar. Please try again.');
            } finally {
                this.uploading(false);
            }
        }
    }

    async uploadCropping() {
        const url = ko.unwrap(this.cropUrl);
        const data = {
            'avatar_cropping': this.coords().join(','),
        };
        try {
            await http.post({url, data});
            this.message('Avatar successfully uploaded.');
            this.error('');
            if (this.params.afterUpload) {
                ko.unwrap(this.params.afterUpload)();
            }
        } catch (e) {
            this.error('Error setting avatar cropping. Please try again.');
        }
    }

    processImage(img) {
        const w = img.width;
        if (w < this.minWidth) {
            this.image(null);
            this.error(
                `Your image must be at least
                ${this.minWidth}x${this.minHeight} pixels.`,
            );
            return;
        }
        const h = img.height;
        if (h < this.minHeight) {
            this.image(null);
            this.error(
                `Your image must be at least
                ${this.minWidth}x${this.minHeight} pixels.`,
            );
            return;
        }
        // Record displayed width so we can make progress bar right size
        this.imageWidth(Math.min(w, this.maxWidth));
        this.imageUrl(img.src);
        // Activate cropping
        let coords;
        if (this.coords()) {
            coords = this.coords();
        } else {
            coords = [0, 0, this.minWidth, this.minHeight];
        }

        setTimeout(() => {
            const preview = this.elem.getElementsByClassName(
                'image-preview',
            )[0];
            const self = this;

            new Cropper(preview, {
                aspectRatio: 1,
                data: {
                    x: coords[0],
                    y: coords[1],
                    width: coords[2] - coords[0],
                    height: coords[3] - coords[1],
                },
                movable: false,
                rotatable: false,
                scalable: false,
                toggleDragModeOnDblclick: false,
                viewMode: 1,
                zoomable: false,
                crop(e) {
                    // set the minimum width and height for the cropbox
                    // must round otherwise recursion errors are encountered
                    if (Math.round(e.detail.width) < self.minWidth) {
                        this.cropper.setData({
                            width: self.minWidth,
                        });
                    }
                    if (Math.round(e.detail.height) < self.minHeight) {
                        this.cropper.setData({
                            height: self.minHeight,
                        });
                    }
                    // get the latest cropper data as it may have changed above
                    const data = this.cropper.getData();
                    self.params.coords([
                        Math.floor(data.x),
                        Math.floor(data.y),
                        Math.floor(data.x + data.width),
                        Math.floor(data.y + data.height),
                    ]);
                },
            });
        });
    }

    processFile(file) {
        this.error('');
        if (this.supportedFileTypes.indexOf(file.type) === -1) {
            this.error(
                'Invalid file type. Please choose a JPEG, PNG, or GIF file.',
            );
            return;
        }
        if (file.size > this.maxFileSize) {
            this.error('Image is too large. Maximum file size is 4MB.');
            return;
        }
        this.image(file);
        this.reader.readAsDataURL(file);
    }

    setFileInput(data, event) {
        this.processFile(event.target.files[0]);
    }

    // Handle file selection by drag and drop
    drop(obj, event) {
        event.stopPropagation();
        if (event.dataTransfer && event.dataTransfer.files) {
            this.processFile(event.dataTransfer.files[0]);
        }
        this.dragCounter(0);
    }

    dragOver(obj, event) {
        event.stopPropagation();
    }

    // Drag enter and leave keep a counter tracking, as enter and leave
    // also fire on all child objects and bubble to here, and it's very hard
    // to tell if event was leaving whole element or entering child.
    // Counting enter / exists will leave us with 0 when drag leaves parent
    dragEnter(obj, event) {
        event.stopPropagation();
        this.dragCounter(this.dragCounter() + 1);
    }

    dragLeave(obj, event) {
        event.stopPropagation();
        this.dragCounter(this.dragCounter() - 1);
    }
}

ko.components.register('image-cropper', {
    viewModel: {
        createViewModel(params, info) {
            return new ImageCropperModel(params, info.element);
        },
    },
    template: imageCropperTemplate,
});
